/**
 * DOM constants + helpers used by the launcher: bubble, badge, iframe
 * creators, style helpers, and the size-resolver. Kept separate from
 * `launcher.ts` so the launcher's state machine stays the focus of the
 * main module — these helpers are pure DOM construction with no shared
 * mutable state.
 *
 * SHARED TOKENS: Sizing and color constants are imported from
 * `@circuit/chat/widget-chrome/tokens` so launcher, SPA, and preview stay
 * in sync. Do NOT import from `@circuit/chat/widget-chrome` (the React
 * path) — that would pull React into the launcher bundle.
 */
import type {
  ChatStyle,
  ConfigPayload,
  SetSizePayload,
} from '@/shared/protocol';

// Shared tokens from @circuit/chat — launcher-safe, no React.
import {
  type CornerPosition,
  BADGE_COLOR,
  BUBBLE_BG_COLOR,
  BUBBLE_LOGO_BG_COLOR,
  closeNudgeSignY,
  cornerAnchor as cornerStyles,
  horizontalAnchor,
  LAUNCHER_GAP_PX as IFRAME_LAUNCHER_CLEARANCE_PX,
  IFRAME_MAX_HEIGHT_PX,
  IFRAME_MAX_WIDTH_PX,
  IFRAME_MIN_HEIGHT_PX,
  IFRAME_MIN_WIDTH_PX,
  IFRAME_VIEWPORT_MARGIN_PX,
  IFRAME_WIDTH_PX,
  LAUNCHER_ICON_SIZE_PX,
  LAUNCHER_INSET_PX,
  LAUNCHER_SIZE_PX,
  PANEL_BG,
  PANEL_BORDER,
  PANEL_RADIUS_PX,
  PANEL_SHADOW,
  verticalAnchor,
} from '@circuit/chat/widget-chrome/tokens';

import {
  BASE_IFRAME_HEIGHT_PX,
  IFRAME_MIN_HEIGHT_PX_FLOATING,
} from '@/shared/iframeSize';

import { createCircuitMarkIcon } from './circuitMarkIcon';

export const BUBBLE_ID = 'circuit-chat-embed-bubble';
export const IFRAME_ID = 'circuit-chat-embed-frame';
export const BADGE_ID = 'circuit-chat-embed-badge';
export const INDICATOR_ID = 'circuit-chat-embed-indicator';
export const LAUNCHER_WRAPPER_ID = 'circuit-chat-embed-launcher-wrapper';
const MAX_Z_INDEX = 2147483647;

/** True when the user has opted out of motion via OS/browser settings. */
const prefersReducedMotion = (): boolean =>
  globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

/**
 * Default iframe height re-exports `BASE_IFRAME_HEIGHT_PX` from the shared
 * module so launcher and SPA can't drift on the base pane height.
 */
export const IFRAME_HEIGHT_PX = BASE_IFRAME_HEIGHT_PX;

function minHeightForStyle(style: ChatStyle): number {
  return style === 'floating'
    ? IFRAME_MIN_HEIGHT_PX_FLOATING
    : IFRAME_MIN_HEIGHT_PX;
}

function clearanceForStyle(style: ChatStyle): number {
  return style === 'windowed' ? IFRAME_LAUNCHER_CLEARANCE_PX : 0;
}

/**
 * Apply a record of CSS string values to an element. Plain `Record` typing
 * keeps the eslint strict checks happy (no for..in, no unnecessary undefined
 * checks). `el.style` indexed assignment is the only param mutation we do;
 * scoped inline disable lives on the one line that needs it.
 */
type StyleRecord = Partial<
  Record<keyof CSSStyleDeclaration, string | undefined>
>;

export function applyStyles(el: HTMLElement, styles: StyleRecord): void {
  for (const [key, value] of Object.entries(styles)) {
    if (value !== undefined) {
      // eslint-disable-next-line no-param-reassign -- mutating el.style is the helper's whole job
      (el.style as unknown as Record<string, string>)[key] = value;
    }
  }
}

export interface BubbleHandlers {
  onClick: () => void;
  /** Fires on hover / focus / pointerdown so the launcher can pre-warm the iframe. */
  onIntent: () => void;
}

/**
 * Build the launcher bubble. `branding.logo` swaps the Circuit mark for an
 * `<img>`; without a logo the bubble renders the Circuit mark on a fixed
 * Circuit-green disc. `branding.primaryColor` is intentionally NOT read here.
 * Sizing and color tokens are shared with the React `<WidgetAvatar>` component.
 */
export function createBubble(
  branding: ConfigPayload['branding'],
  agentName: string,
  handlers: BubbleHandlers,
): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.id = BUBBLE_ID;
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Open chat');
  btn.setAttribute('aria-expanded', 'false');
  applyStyles(btn, {
    alignItems: 'center',
    backgroundColor: branding.logo ? BUBBLE_LOGO_BG_COLOR : BUBBLE_BG_COLOR,
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    height: `${LAUNCHER_SIZE_PX}px`,
    justifyContent: 'center',
    overflow: 'hidden',
    padding: '0',
    width: `${LAUNCHER_SIZE_PX}px`,
  });
  btn.appendChild(createBubbleIcon(branding.logo, agentName));
  btn.addEventListener('click', handlers.onClick);
  // Pre-warm triggers — hover/focus/pointerdown all precede a click and let
  // the iframe start loading before the user commits. onIntent is idempotent.
  btn.addEventListener('mouseenter', handlers.onIntent);
  btn.addEventListener('focus', handlers.onIntent);
  btn.addEventListener('pointerdown', handlers.onIntent);
  return btn;
}

/**
 * Build the bubble's foreground.
 *
 * - `logoUrl === null` → the inline Circuit mark SVG (white on green disc).
 * - `logoUrl` set → full-bleed `<img>` filling the 64×64 bubble, circle-
 *   cropped by the parent button's `border-radius: 50%` + `overflow: hidden`.
 *   Broken URL → falls back to the Circuit mark on the neutral bg.
 */
function createBubbleIcon(
  logoUrl: string | null,
  agentName: string,
): HTMLElement | SVGSVGElement {
  if (logoUrl === null) {
    return createCircuitMarkIcon(LAUNCHER_ICON_SIZE_PX);
  }
  const img = document.createElement('img');
  img.src = logoUrl;
  img.alt = agentName;
  applyStyles(img, {
    height: '100%',
    objectFit: 'cover',
    width: '100%',
  });
  img.addEventListener('error', () => {
    const fallback = createCircuitMarkIcon(LAUNCHER_ICON_SIZE_PX);
    img.replaceWith(fallback);
  });
  return img;
}

/**
 * Small circular open/close indicator overlaid on the bubble's outer
 * corner. Chevron-up when closed, X when open. Pointer events pass through
 * to the bubble button below.
 */
export function createIndicator(
  position: CornerPosition,
  isOpen: boolean,
): HTMLSpanElement {
  const el = document.createElement('span');
  el.id = INDICATOR_ID;
  el.setAttribute('aria-hidden', 'true');
  const INDICATOR_SIZE_PX = 32;
  // Overlap the bubble by 3/4 of the indicator so it sits mostly inside the
  // bubble with just a small nub protruding — matches the design.
  const outset = INDICATOR_SIZE_PX / 4;
  const vAnchor = verticalAnchor(position);
  const hAnchor = horizontalAnchor(position);
  // Bottom-anchored bubbles put the indicator on the bubble's TOP (facing
  // the viewport interior); top-anchored on the BOTTOM; middle-row defaults
  // to TOP. Horizontally the nub protrudes AWAY from the viewport edge the
  // bubble hugs — right-anchored/center bubbles get the indicator on their
  // RIGHT; left-anchored bubbles get it on their LEFT.
  const hugRight = hAnchor !== 'left';
  applyStyles(el, {
    alignItems: 'center',
    backgroundColor: '#000000',
    borderRadius: '999px',
    bottom: vAnchor === 'top' ? `${-outset}px` : 'auto',
    color: '#ffffff',
    display: 'flex',
    height: `${INDICATOR_SIZE_PX}px`,
    justifyContent: 'center',
    left: hugRight ? 'auto' : `${-outset}px`,
    pointerEvents: 'none',
    position: 'absolute',
    right: hugRight ? `${-outset}px` : 'auto',
    top: vAnchor === 'top' ? 'auto' : `${-outset}px`,
    width: `${INDICATOR_SIZE_PX}px`,
  });

  // dataset.vAnchor <-> HTML attr `data-v-anchor`; picked at create time so
  // `setIndicatorOpen` can regenerate the correct chevron direction without
  // threading vAnchor through every call site.
  el.dataset.vAnchor = vAnchor;
  el.appendChild(createIndicatorIcon(isOpen, vAnchor));
  return el;
}

/**
 * Fixed-position wrapper that owns the launcher's corner anchor. Bubble +
 * indicator live inside; the bubble is absolute so its `overflow: hidden`
 * logo crop stays intact without clipping the sibling indicator.
 */
export function wrapLauncherBubble(
  bubble: HTMLButtonElement,
  indicator: HTMLSpanElement,
  position: CornerPosition,
): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.id = LAUNCHER_WRAPPER_ID;
  applyStyles(wrapper, {
    height: `${LAUNCHER_SIZE_PX}px`,
    pointerEvents: 'none',
    position: 'fixed',
    width: `${LAUNCHER_SIZE_PX}px`,
    zIndex: String(MAX_Z_INDEX),
    ...cornerStyles(position),
  });
  applyStyles(bubble, {
    bottom: 'auto',
    left: '0',
    pointerEvents: 'auto',
    position: 'absolute',
    right: 'auto',
    top: '0',
    zIndex: '0',
  });
  wrapper.appendChild(bubble);
  wrapper.appendChild(indicator);
  return wrapper;
}

/**
 * Toggle the indicator glyph between the chevron-up (closed) and X (open)
 * states without recreating the whole element.
 */
export function setIndicatorOpen(el: HTMLSpanElement, isOpen: boolean): void {
  // Preserve the vAnchor picked at createIndicator time; stored via
  // dataset so we don't need to thread it through every call site.
  const vAnchor: 'bottom' | 'top' =
    el.dataset.vAnchor === 'top' ? 'top' : 'bottom';
  el.replaceChildren(createIndicatorIcon(isOpen, vAnchor));
}

/** Keep the bubble's accessible name in sync with its toggle state. */
export function setBubbleOpen(btn: HTMLButtonElement, isOpen: boolean): void {
  btn.setAttribute('aria-label', isOpen ? 'Close chat' : 'Open chat');
  btn.setAttribute('aria-expanded', String(isOpen));
}

function createIndicatorIcon(
  isOpen: boolean,
  vAnchor: 'bottom' | 'top' = 'bottom',
): SVGSVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  const path = document.createElementNS(NS, 'path');
  // Closed-state chevron points AWAY from the bubble toward where the
  // widget will appear — bottom-anchored bubbles (widget grows up) get
  // chevron-up; top-anchored bubbles (widget grows down) get chevron-down.
  // Open state is always an X regardless of anchor.
  let pathD: string;
  if (isOpen) {
    pathD = 'M5 5 L15 15 M15 5 L5 15';
  } else if (vAnchor === 'top') {
    pathD = 'M5 8 L10 13 L15 8';
  } else {
    pathD = 'M5 12 L10 7 L15 12';
  }
  path.setAttribute('d', pathD);
  svg.appendChild(path);
  return svg;
}

/**
 * Create the unread-count badge as a free-floating element pinned to the
 * top-right of the bubble. Detached from the bubble button so we can fade
 * the bubble's visibility (when the chat is open) without losing badge
 * state — the badge is just hidden alongside the bubble in that case.
 */
export function createBadge(position: CornerPosition): HTMLSpanElement {
  const badge = document.createElement('span');
  badge.id = BADGE_ID;
  badge.setAttribute('aria-hidden', 'true');
  const hAnchor = horizontalAnchor(position);
  const vAnchor = verticalAnchor(position);
  // Badge is `position: fixed` (detached from the bubble/wrapper) so it can
  // stay visible while the bubble fades on widget-open. Compute a badge
  // position on the OPPOSITE corner from the open/close indicator so the
  // two never overlay each other.
  //
  // Indicator placement (from createIndicator):
  //   H: RIGHT when hAnchor !== 'left' (center + right cols), LEFT otherwise.
  //   V: BOTTOM when vAnchor === 'top', TOP otherwise (center + bottom rows).
  //
  // Badge goes on the opposite corner:
  //   H: LEFT when indicator is RIGHT (center + right cols), RIGHT otherwise.
  //   V: TOP when indicator is BOTTOM (top rows), BOTTOM otherwise.
  //
  // Edge-anchored axes use a fixed pixel offset from the viewport edge.
  // Center-anchored axes use calc(50% ± Npx) to track the centered bubble.
  const BADGE_INNER_OFFSET_PX = 20;
  const edgeInner = `${LAUNCHER_INSET_PX + LAUNCHER_SIZE_PX - BADGE_INNER_OFFSET_PX}px`;
  // Badge H/V: opposite edge from the indicator so they never overlap.
  // `hAnchor` is 'left' | 'right' and `vAnchor` is 'top' | 'bottom' (see
  // positioning.ts); both dims collapse to `edgeInner`.
  const isLeftSide = hAnchor === 'left';
  const isTopSide = vAnchor === 'top';
  const badgeH = edgeInner;
  const badgeV = edgeInner;
  applyStyles(badge, {
    alignItems: 'center',
    backgroundColor: BADGE_COLOR,
    borderRadius: '999px',
    bottom: isTopSide ? 'auto' : badgeV,
    color: '#ffffff',
    display: 'none',
    fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    fontSize: '11px',
    fontWeight: '700',
    height: '20px',
    justifyContent: 'center',
    left: isLeftSide ? badgeH : 'auto',
    lineHeight: '1',
    minWidth: '20px',
    padding: '0 6px',
    pointerEvents: 'none',
    position: 'fixed',
    right: isLeftSide ? 'auto' : badgeH,
    top: isTopSide ? badgeV : 'auto',
    visibility: 'visible',
    zIndex: String(MAX_Z_INDEX),
  });
  return badge;
}

/**
 * Render the badge for a given count, or hide it when count <= 0.
 * Mutates `badge` — a single function-level disable is cleaner than
 * per-line ones.
 */
/* eslint-disable no-param-reassign -- badge mutation is this helper's job */
export function renderBadge(badge: HTMLSpanElement, count: number): void {
  if (count <= 0 || !Number.isFinite(count)) {
    badge.textContent = '';
    applyStyles(badge, { display: 'none' });
    return;
  }
  const clamped = Math.floor(count);
  badge.textContent = clamped > 99 ? '99+' : String(clamped);
  applyStyles(badge, { display: 'flex' });
}
/* eslint-enable no-param-reassign */

/**
 * Iframe corner styles, shifted inward on the anchored vertical edge so the
 * iframe clears the launcher bubble instead of overlapping it. Bottom-anchored
 * iframes sit above the bubble; top-anchored below.
 *
 * When `omitLauncherChrome` is true (openByDefault path, no bubble rendered),
 * only the edge inset is reserved — the bubble size and per-style clearance are
 * zeroed out so the pane sits flush against the viewport edge inset.
 */
export function iframeCornerStyles(
  position: CornerPosition,
  style: ChatStyle = 'floating',
  omitLauncherChrome = false,
): {
  bottom: string;
  left: string;
  right: string;
  top: string;
} {
  const corner = cornerStyles(position);
  const chromePx = omitLauncherChrome
    ? 0
    : LAUNCHER_SIZE_PX + clearanceForStyle(style);
  const clearance = `${LAUNCHER_INSET_PX + chromePx}px`;
  const vAnchor = verticalAnchor(position);
  if (vAnchor === 'top') {
    return { ...corner, bottom: 'auto', top: clearance };
  }
  return { ...corner, bottom: clearance, top: 'auto' };
}

export function createIframe(
  src: string,
  position: CornerPosition,
  style: ChatStyle = 'windowed',
  omitLauncherChrome = false,
): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  iframe.id = IFRAME_ID;
  iframe.src = src;
  iframe.title = 'Circuit Chat';
  // allow-popups + allow-popups-to-escape-sandbox: assistant responses can
  // link out (docs, article references); without these, `target="_blank"`
  // silently no-ops. `allow-same-origin` is safe because the SPA is served
  // from a dedicated origin (chat-embed.circuit.ai) that customers don't
  // otherwise script against; do not enable this on same-origin deployments.
  iframe.setAttribute(
    'sandbox',
    'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox',
  );
  // Windowed = opaque bordered pane w/ modal shadow (chrome lives on the
  // iframe itself). Floating = transparent iframe; the SPA paints its own
  // floating cards for the header / chat area / prompt so pieces appear to
  // hover over the host page. Both anchor via `branding.position`.
  const isWindowed = style === 'windowed';
  // Windowed pane uses shared panel tokens from @circuit/chat/widget-chrome/tokens.
  const backgroundColor = isWindowed ? PANEL_BG : 'transparent';
  const border = isWindowed ? `1px solid ${PANEL_BORDER}` : 'none';
  const boxShadow = isWindowed ? PANEL_SHADOW : 'none';
  const borderRadius = isWindowed ? `${PANEL_RADIUS_PX}px` : '0';
  const baseWidth = IFRAME_WIDTH_PX;
  const baseHeight = IFRAME_HEIGHT_PX;
  const chromePx = omitLauncherChrome
    ? 0
    : LAUNCHER_SIZE_PX + clearanceForStyle(style);
  applyStyles(iframe, {
    backgroundColor,
    border,
    borderRadius,
    boxShadow,
    height: `min(${baseHeight}px, calc(100vh - ${LAUNCHER_INSET_PX + chromePx + IFRAME_VIEWPORT_MARGIN_PX}px))`,
    opacity: '0',
    pointerEvents: 'none',
    position: 'fixed',
    transform: `translateY(${closeNudgeSignY(position) * 20}px)`,
    transformOrigin: `${verticalAnchor(position)} ${horizontalAnchor(position)}`,
    visibility: 'hidden',
    width: `min(${baseWidth}px, calc(100vw - ${IFRAME_VIEWPORT_MARGIN_PX * 2}px))`,
    zIndex: String(MAX_Z_INDEX - 1),
    transition: prefersReducedMotion()
      ? 'none'
      : 'opacity 220ms ease-out, ' +
        'transform 280ms cubic-bezier(0.22, 1, 0.36, 1), ' +
        'width 280ms cubic-bezier(0.22, 1, 0.36, 1), ' +
        'height 280ms cubic-bezier(0.22, 1, 0.36, 1), ' +
        'border-radius 220ms ease-out',
    ...iframeCornerStyles(position, style, omitLauncherChrome),
  });
  return iframe;
}

/**
 * Numeric clamp that gracefully collapses when `max < min`. In that case we
 * pin to `max` — relevant on tiny viewports where the available height after
 * reserving launcher chrome falls below IFRAME_MIN_HEIGHT_PX; without this,
 * `Math.max(MIN, ...)` would push the iframe past the launcher.
 */
function clamp(value: number, min: number, max: number): number {
  if (max < min) return max;
  return Math.max(min, Math.min(max, value));
}

/** Track the most recent SET_SIZE so HEIGHT_CHANGE can resize height alone. */
export interface SizeState {
  height: number;
  placement: 'center' | 'corner';
  width: number;
}

/**
 * Apply a SPA-requested iframe size + placement. Width/height are clamped
 * to MIN/MAX and the viewport. Returns the resolved size so callers can
 * store it for HEIGHT_CHANGE / fullscreen restoration.
 */
export function applyIframeSize(
  iframe: HTMLIFrameElement,
  payload: SetSizePayload,
  cornerPosition: CornerPosition,
  style: ChatStyle = 'windowed',
  omitLauncherChrome = false,
): SizeState {
  // Reserve the launcher's chrome (inset + bubble + clearance) below the
  // iframe and a viewport margin above so the pane doesn't stretch edge-to-
  // edge. `Math.max(0, ...)` on both dims guards against a viewport small
  // enough that the reserved chrome exceeds it — without the floor, the
  // fill-height branch below could size the iframe to a negative pixel value.
  // When omitLauncherChrome is true (openByDefault path, no bubble), only the
  // edge inset is reserved — the bubble size and per-style clearance are omitted.
  const chromePx = omitLauncherChrome
    ? 0
    : LAUNCHER_SIZE_PX + clearanceForStyle(style);
  const viewportWidth = Math.max(
    0,
    globalThis.innerWidth - IFRAME_VIEWPORT_MARGIN_PX * 2,
  );
  const viewportHeight = Math.max(
    0,
    globalThis.innerHeight -
      (LAUNCHER_INSET_PX + chromePx) -
      IFRAME_VIEWPORT_MARGIN_PX,
  );
  // Fill-viewport sentinel: a non-finite value or any value STRICTLY below
  // MIN is the SPA's way of asking for available viewport. postMessage can't
  // carry `Infinity` so the SPA uses `0` as the agreed sentinel; a value
  // equal to MIN is honored as MIN, not viewport. Height MIN varies by style
  // so floating can auto-shrink to a prompt-only pane while windowed keeps
  // its 480px pane floor.
  const minHeight = minHeightForStyle(style);
  const wantsFillWidth =
    !Number.isFinite(payload.width) || payload.width < IFRAME_MIN_WIDTH_PX;
  const wantsFillHeight =
    !Number.isFinite(payload.height) || payload.height < minHeight;
  const nextWidth = wantsFillWidth
    ? viewportWidth
    : clamp(
        Math.round(payload.width),
        IFRAME_MIN_WIDTH_PX,
        Math.min(IFRAME_MAX_WIDTH_PX, viewportWidth),
      );
  const nextHeight = wantsFillHeight
    ? viewportHeight
    : clamp(
        Math.round(payload.height),
        minHeight,
        Math.min(IFRAME_MAX_HEIGHT_PX, viewportHeight),
      );
  const placement: 'center' | 'corner' =
    payload.placement === 'center' ? 'center' : 'corner';
  // Reset border-radius on every resize — a prior FULLSCREEN_REQUEST may
  // have zeroed it, and SET_SIZE / HEIGHT_CHANGE implicitly exit fullscreen.
  // Windowed uses the 20px pane radius; floating is a transparent surface
  // so the iframe itself has no radius (each floating card carries its own).
  applyStyles(iframe, {
    borderRadius: style === 'windowed' ? '20px' : '0',
    height: `${nextHeight}px`,
    width: `${nextWidth}px`,
  });
  applyStyles(
    iframe,
    iframeCornerStyles(cornerPosition, style, omitLauncherChrome),
  );
  const transform = centerTransform({
    cornerPosition,
    height: nextHeight,
    omitLauncherChrome,
    placement,
    style,
    width: nextWidth,
  });
  applyStyles(iframe, { transform });
  return { height: nextHeight, placement, width: nextWidth };
}

/** Center-mode translation offset in px. `x` may be negative (bottom-right
 *  corner shifts leftward to reach center); `y` is always <= 0 (upward). */
export interface CenterOffset {
  x: number;
  y: number;
}

export interface CenterArgs {
  cornerPosition: CornerPosition;
  height: number;
  /** When true (openByDefault path), omit bubble size + clearance from the
   * vertical edge offset so the center transform aligns correctly with the
   * reduced iframeCornerStyles clearance.
   */
  omitLauncherChrome?: boolean;
  placement: 'center' | 'corner';
  style?: ChatStyle;
  width: number;
}

export function centerOffset({
  cornerPosition,
  height,
  omitLauncherChrome = false,
  placement,
  style = 'floating',
  width,
}: CenterArgs): CenterOffset {
  if (placement !== 'center') return { x: 0, y: 0 };
  // Horizontal edges stay pinned at LAUNCHER_INSET_PX by iframeCornerStyles;
  // vertical edges are pushed inward by inset + launcher size + clearance so
  // the iframe clears the bubble. The translate has to unwind the SAME edge
  // offsets to land the iframe on the viewport midline.
  const chromePx = omitLauncherChrome
    ? 0
    : LAUNCHER_SIZE_PX + clearanceForStyle(style);
  const verticalEdgeOffset = LAUNCHER_INSET_PX + chromePx;
  const offsetX =
    Math.max(
      IFRAME_VIEWPORT_MARGIN_PX,
      Math.round((globalThis.innerWidth - width) / 2),
    ) - LAUNCHER_INSET_PX;
  const offsetY =
    Math.max(
      IFRAME_VIEWPORT_MARGIN_PX,
      Math.round((globalThis.innerHeight - height) / 2),
    ) - verticalEdgeOffset;
  // Sign is driven by which viewport edge cornerStyles pinned us to: a
  // left-anchored launcher translates rightward toward center; a
  // right-anchored one translates leftward; a horizontally-centered launcher
  // (any *-center position) needs no x shift because it is already on the
  // horizontal midline. Same for vertical.
  const hAnchor = horizontalAnchor(cornerPosition);
  const vAnchor = verticalAnchor(cornerPosition);
  const signX = ((): number => {
    if (hAnchor === 'left') return 1;
    if (hAnchor === 'right') return -1;
    return 0;
  })();
  const signY = ((): number => {
    if (vAnchor === 'top') return 1;
    if (vAnchor === 'bottom') return -1;
    return 0;
  })();
  return { x: signX * offsetX, y: signY * offsetY };
}

/** String form of {@link centerOffset} for direct use as a CSS transform. */
export function centerTransform(args: CenterArgs): string {
  const { x, y } = centerOffset(args);
  if (x === 0 && y === 0) return 'translateY(0)';
  return `translate(${x}px, ${y}px)`;
}

/** Edge-to-edge iframe used by FULLSCREEN_REQUEST. */
export function applyFullscreen(iframe: HTMLIFrameElement): void {
  applyStyles(iframe, {
    borderRadius: '0',
    bottom: '0',
    height: '100%',
    left: '0',
    right: '0',
    top: '0',
    transform: 'translateY(0)',
    width: '100%',
  });
}
